import { SeasonGroup, WaterbodyDetail } from '@omniafishing/core';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryString } from '../../../hooks/use_query_string';
import { useUserPreferences } from '../../../hooks/use_user_preferences';
import { useUserProfile } from '../../../hooks/use_user_profile';
import { getSpecies, SEASON_GROUP_NAME_ICE } from '../../../redux/reference_data';
import { ExtendedMapQueryParams } from '../../../routes/map_page/lib/map_page_types';
import { WaterbodyDetailQueryParams } from '../waterbody_detail';
import { determineSpeciesName, speciesNotAllowedInIceSeason } from './determine_species';

export const determineSeasonGroup = (params: {
  seasonGroups: SeasonGroup[];
  querySeasonGroup: string;
  userPreferencesSeasonGroup: string;
  currentSeasonGroup: SeasonGroup;
}) => {
  const { seasonGroups, querySeasonGroup, userPreferencesSeasonGroup, currentSeasonGroup } = params;
  const queryParamSeasonGroup = seasonGroups.find((sg) => sg.name === querySeasonGroup);
  const userPrefsSeasonGroup = seasonGroups.find((sg) => sg.name === userPreferencesSeasonGroup);
  return queryParamSeasonGroup || userPrefsSeasonGroup || currentSeasonGroup;
};

export const useWaterbodyState = (props: { waterbody: WaterbodyDetail }) => {
  const { waterbody } = props;

  const { getCurrentQuery, replaceQueryString } = useQueryString();
  const currentQuery = getCurrentQuery<WaterbodyDetailQueryParams>();
  const { userProfile } = useUserProfile();
  const { userPreferencesSeasonGroup } = useUserPreferences();
  const allSpecies = useSelector(getSpecies);

  const { seasons, species, season_groups } = waterbody;
  const currentSeason = seasons.find((s) => s.active);
  const currentSeasonGroup = season_groups.find((sg) => sg.active);

  const determinedSeasonGroup = determineSeasonGroup({
    seasonGroups: season_groups,
    querySeasonGroup: currentQuery.season_group,
    userPreferencesSeasonGroup,
    currentSeasonGroup,
  });
  // species has a dependency on season, so season has to come first
  const determinedSpeciesName =
    currentQuery.species ||
    determineSpeciesName({
      selectedSeasonGroupName: determinedSeasonGroup.name,
      userPreferencesTopSpeciesName: null,
      userProfileSpecies: userProfile?.species,
      waterbody,
    });

  const [selectedSeasonGroup, setSelectedSeasonGroup] = useState(determinedSeasonGroup);
  const [selectedSpeciesName, setSelectedSpeciesName] = useState(determinedSpeciesName);
  const selectedSpecies = allSpecies.find((s) => s.name === selectedSpeciesName);
  const waterbodyHasIceSpecies = species.some(
    (s) => !speciesNotAllowedInIceSeason.includes(s.name)
  );
  const waterbodySpeciesFiltered =
    determinedSeasonGroup.name === SEASON_GROUP_NAME_ICE && waterbodyHasIceSpecies
      ? species.filter((s) => !speciesNotAllowedInIceSeason.includes(s.name))
      : species;
  const waterbodySpeciesSorted = _.sortBy(waterbodySpeciesFiltered, ['recommendations_order']);

  useEffect(() => {
    if (!_.isEqual(selectedSeasonGroup, determinedSeasonGroup)) {
      setSelectedSeasonGroup(determinedSeasonGroup);
    }
    if (!_.isEqual(selectedSpeciesName, determinedSpeciesName)) {
      setSelectedSpeciesName(determinedSpeciesName);
    }
  }, [currentQuery.season_group, currentQuery.species, userProfile?.slug]);

  // clear non-ice species
  useEffect(() => {
    if (
      selectedSeasonGroup.name === SEASON_GROUP_NAME_ICE &&
      speciesNotAllowedInIceSeason.includes(selectedSpeciesName)
    ) {
      replaceQueryString<ExtendedMapQueryParams>({ species: null });
      setSelectedSpeciesName(waterbodySpeciesSorted[0].name);
    }
  }, [selectedSeasonGroup.name, selectedSpeciesName]);

  return {
    currentSeason,
    currentSeasonGroup,
    selectedSeasonGroup,
    selectedSpecies,
    waterbodySpeciesSorted,
  };
};
